























































































































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import StatementConciliationRepository from '@/repositories/StatementConciliationRepository';
import AcquittanceReasonOption from '@/domain/models/AcquittanceReasonOptions';

interface FileData {
  cnpj: string;
  numeroTitulo: string;
  prefixo: string;
  tipo: string;
  loja: string;
  valorTitulo: number;
  juros: number;
  desconto: number;
  valorPago: number;
}

@Component({})
export default class WriteOffBatchImport extends Vue {
  @Prop(Boolean) readonly open!: boolean;
  @Prop(Array) readonly selectedItems!: any[];
  @Prop(Number) readonly companyId!: number;
  @Prop(String) readonly bankId!: string;

  readonly StatementConciliationRepository:
    StatementConciliationRepository = new StatementConciliationRepository();

  selectedFile: File | null = null;
  uploading: boolean = false;
  isHovered: boolean = false;
  isDragOver: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  fileData: FileData[] = [];

  reason: string = '';
  reasons: Array<{ text: string; value: string }> = [];
  reasonLoading: boolean = false;
  history: string = '';
  valueRequiredRule = (value: string) => !!value || 'Campo obrigatório!';

  tableHeaders = [
    { text: 'CNPJ do Cliente', value: 'cnpj', sortable: true },
    { text: 'Número do Título', value: 'numeroTitulo', sortable: true },
    { text: 'Prefixo', value: 'prefixo', sortable: true },
    { text: 'Tipo', value: 'tipo', sortable: true },
    { text: 'Loja', value: 'loja', sortable: true },
    { text: 'Valor do Título', value: 'valorTitulo', sortable: true },
    { text: 'Juros', value: 'juros', sortable: true },
    { text: 'Desconto', value: 'desconto', sortable: true },
    { text: 'Valor Pago', value: 'valorPago', sortable: true },
  ];

  get groupId(): number {
    return this.$session.get('company_group_id');
  }

  mounted() {
    this.loadReasons();
  }

  async loadReasons(): Promise<void> {
    try {
      this.reasonLoading = true;
      const reasons = await this.StatementConciliationRepository
        .getReasons(this.groupId, this.companyId);
      this.reasons = reasons.map((reason: AcquittanceReasonOption) => {
        const { description: text, erpCode: value } = reason;
        return { text, value };
      });
    } catch (error) {
      this.$notification.error('Não foi possível carregar os motivos de baixa.');
    } finally {
      this.reasonLoading = false;
    }
  }

  triggerFileInput(): void {
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  handleFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      [this.selectedFile] = target.files;
      this.processFile(this.selectedFile);
    }
  }

  async processFile(file: File): Promise<void> {
    this.selectedFile = file;
    this.errorMessage = '';
    this.successMessage = '';
    this.fileData = [];

    try {
      const filename = (file.name || '').toLowerCase();
      const ext = filename.split('.').pop() || '';

      if (ext === 'xls' || ext === 'xlsx') {
        this.errorMessage = 'Arquivos .xls/.xlsx não são processados no navegador. Salve como CSV (Arquivo -> Salvar como -> CSV) e importe.';
        return;
      }

      const text = await this.readFileAsText(file);
      const data = this.parseFileData(text);
      if (data.length === 0) {
        this.errorMessage = 'Arquivo lido, mas nenhum registro válido foi encontrado. Verifique o formato (CSV) e o separador (vírgula ou ponto-e-vírgula).';
      } else {
        this.fileData = data;
      }
    } catch (error) {
      this.errorMessage = 'Erro ao processar arquivo';
      console.error('Erro ao processar arquivo:', error);
    }
  }

  readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  parseFileData(text: string): FileData[] {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    const data: FileData[] = [];

    if (lines.length <= 1) return data;

    let sep = ',';
    const header = lines[0];
    if (header.includes(';')) sep = ';';
    else if (header.includes('\t')) sep = '\t';
    else if (!header.includes(',') && lines.length > 1) {
      const second = lines[1];
      if (second.includes(';')) sep = ';';
      else if (second.includes('\t')) sep = '\t';
    }

    for (let i = 1; i < lines.length; i += 1) {
      const values = lines[i].split(sep).map((v) => v.trim());
      if (values.length >= 9) {
        data.push({
          cnpj: values[0] || '',
          numeroTitulo: values[1] || '',
          prefixo: values[2] || '',
          tipo: values[3] || '',
          loja: values[4] || '',
          valorTitulo: parseFloat(values[5]) || 0,
          juros: parseFloat(values[6]) || 0,
          desconto: parseFloat(values[7]) || 0,
          valorPago: parseFloat(values[8]) || 0,
        });
      }
    }

    return data;
  }

  formatCNPJ(cnpj: string): string {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  async uploadFile(): Promise<void> {
    if (!this.selectedFile || this.fileData.length === 0) {
      this.errorMessage = 'Nenhum arquivo válido selecionado';
      return;
    }
    if (!this.reason) {
      this.errorMessage = 'Selecione o motivo da baixa.';
      return;
    }

    const historyToSend = this.history && this.history.trim() !== ''
      ? this.history
      : 'Valor recebido s/ título InnCash';

    try {
      this.uploading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const receivables = this.fileData.map((item) => ({
        client: item.cnpj,
        num: item.numeroTitulo,
        prefix: item.prefixo,
        type: item.tipo,
        store: item.loja,
        value: item.valorTitulo,
        assessment: item.juros,
        discount: item.desconto,
        received_value: item.valorPago,
        tax: 0,
      }));
      const { bankId } = this;
      const bankMovements = this.selectedItems.map((item) => {
            const { id, date: dispositionDate } = item;
            return {
              dispositionDate,
              id: +id,
            };
          });
      const data = {
        bank_id: bankId,
        company_id: this.companyId,
        reason: this.reason,
        history: historyToSend,
        receivables,
        bank_movements: bankMovements,
        disposition_date:
          this.selectedItems
            && this.selectedItems.length > 0 ? this.selectedItems[0].date : undefined,
      };

      const success = await this.StatementConciliationRepository
        .writeOffBatchImportMovement(this.groupId, this.companyId, data);

      if (success) {
        this.successMessage = 'Arquivo processado com sucesso!';
        this.$notification.success('Títulos baixados em lote com sucesso!');
        this.reason = '';
        this.history = '';
        this.close();
        this.$emit('reload');
      } else {
        this.errorMessage = 'Erro ao processar arquivo';
      }
    } catch (error: any) {
      console.error('Erro no upload:', error);
      this.errorMessage = error.response?.data?.message || 'Erro ao enviar arquivo';
    } finally {
      this.uploading = false;
    }
  }

  handleProcessClick() {
    if (this.fileData.length === 0) {
      this.$notification.warn('Importe um arquivo válido.');
      return;
    }
    if (!this.reason) {
      this.$notification.warn('Selecione o motivo da baixa.');
      return;
    }
    if (!this.uploading) {
      this.uploadFile();
    }
  }

  clearFile(): void {
    this.selectedFile = null;
    this.fileData = [];
    this.errorMessage = '';
    this.successMessage = '';
    this.isDragOver = false;
    this.isHovered = false;
  }

  close(): void {
    this.selectedFile = null;
    this.fileData = [];
    this.errorMessage = '';
    this.successMessage = '';
    this.uploading = false;
    this.isDragOver = false;
    this.reason = '';
    this.history = '';
    this.$emit('close');
  }
}
